import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Stack, useTheme } from '@mui/material';
import { IconProps, List as ListIcon, X } from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import { ROUTES } from 'theSource/app/routes';
import { InternalPage, List } from 'theSource/components';
import { Entries } from 'theSource/types';
import { ADDITIONAL_NAVIGATION_ICONS } from 'theSource/utils';
import {
  DrawerStyled,
  IconButtonStyled,
  MenuWrapperStyled,
  CloseButtonStyled,
} from './DrawerStyled';
import { LogoutButton } from 'theSource/components/LogoutButton';
import { AdminHomePageButton } from 'theSource/components/AdminHomepageButton';
import { selectIsAdmin } from 'theSource/features';

export const Drawer = () => {
  const isAdmin = useSelector(selectIsAdmin);
  const [isOpen, setIsOpen] = useState(false);
  const { t } = useTranslation('pages.more');
  const location = useLocation();
  const theme = useTheme();

  const iconProps: IconProps = {
    size: 24,
    color: theme.palette.primary.main,
    weight: 'bold',
  };

  const toggleDrawer = () => setIsOpen(!isOpen);
  const closeDrawer = () => setIsOpen(false);

  useEffect(() => {
    closeDrawer();
  }, [location]);

  return (
    <>
      <IconButtonStyled
        data-testid="drawer-open-button"
        onClick={toggleDrawer}
        aria-haspopup="true"
        aria-expanded="false"
      >
        {!isOpen && <ListIcon {...iconProps} />}
      </IconButtonStyled>
      <DrawerStyled
        data-testid="drawer"
        anchor="right"
        open={isOpen}
        onClose={closeDrawer}
      >
        <CloseButtonStyled
          data-testid="drawer-close-button"
          onClick={toggleDrawer}
          aria-haspopup="true"
          aria-expanded="true"
        >
          <X {...iconProps} />
        </CloseButtonStyled>
        <MenuWrapperStyled>
          <InternalPage testId="more-page" title={t('moreWindowTitle')}>
            <Stack spacing={5}>
              {(
                Object.entries(ROUTES.additional) as Entries<
                  typeof ROUTES.additional
                >
              ).map(([heading, routes], index) => (
                <List
                  key={index}
                  subheaderInfo={{
                    icon: ADDITIONAL_NAVIGATION_ICONS[heading],
                    title: heading,
                  }}
                  items={Object.values(routes)}
                />
              ))}
              {isAdmin && <AdminHomePageButton dataTestId="admin-home-page-button-desktop" />}
              <LogoutButton dataTestId="log-out-button" />
            </Stack>
          </InternalPage>
        </MenuWrapperStyled>
      </DrawerStyled>
    </>
  );
};
